/** Референс карточки «под капотом» (19.09): ссылка на ПРОСМОТР.
 *
 *  Публичная витрина ссылку больше не отдаёт (её забирали curl'ом, не заходя на сайт),
 *  а в генерацию файл подставляет сервер по source_card_id. Здесь — короткая подпись
 *  (10 минут) для показа человеку: качество полное, но постоянного адреса у файла нет.
 *  В payload эта ссылка не уезжает — там флаг card_ref_video / card_ref_audio.
 */
import api from './client'

export type CardReferenceKind = 'video' | 'image' | 'audio'

const cache = new Map<string, Promise<string | null>>()

// Паузы перед повторами после сбоя (обрыв сети, 5xx): виджет открыт, человек ещё выбирает фото.
const RETRY_DELAYS_MS = [1200, 3000]

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

/** Окончательное «референса нет»: файла нет или админ снял «показ» (404), файл не в нашем хранилище (409). */
function isFinalMiss(err: unknown): boolean {
	const status = (err as { status?: number } | null)?.status
	return status === 404 || status === 409
}

async function loadReferenceUrl(sourceId: string, kind: CardReferenceKind): Promise<string | null> {
	for (let attempt = 0; ; attempt++) {
		try {
			const r = await api.request<{ ok: boolean; url: string }>(
				`/api/v1/cards/${encodeURIComponent(sourceId)}/reference/${kind}`,
			)
			return r?.url || null
		} catch (err) {
			if (isFinalMiss(err)) return null
			// Сессии нет (гость, истёкший вход) — повтор ничего не изменит.
			if (attempt >= RETRY_DELAYS_MS.length || !api.isAuthenticated()) throw err
			await sleep(RETRY_DELAYS_MS[attempt])
		}
	}
}

/** Ссылка на просмотр или null (референса нет, «показ» снят). Гостю её не выдают — за ней ходим только
 *  со входом (GenerateToolMode): запрос без сессии уводил человека с лендинга тренда на страницу входа.
 *
 *  Запоминаем на время жизни вкладки только ответ — ссылку или окончательное «референса нет»: эффекты
 *  подстановки дёргаются при каждом сбросе загрузок. Сбой (обрыв сети, 5xx) — не ответ: его не помним.
 *  Раньше null после одного сбоя оставался до перезагрузки страницы: трек карточки молча не подставлялся,
 *  флаг card_ref_audio не уходил, и ролик генерировался без музыки тренда (30.09). */
export function cardReferenceUrl(sourceId: string, kind: CardReferenceKind): Promise<string | null> {
	const key = `${sourceId}:${kind}`
	const hit = cache.get(key)
	if (hit) return hit
	const req = loadReferenceUrl(sourceId, kind).catch(() => {
		if (cache.get(key) === req) cache.delete(key)
		return null
	})
	cache.set(key, req)
	return req
}

/** Длина и кадр видео тренда (замер на сервере; чего не узнали — null). Ссылку на файл ручка не отдаёт. */
export interface CardReferenceMeta {
	seconds: number | null
	width: number | null
	height: number | null
}

const metaCache = new Map<string, Promise<CardReferenceMeta | null>>()

const positive = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null)

/** Seedance 2.5 видео тренда переделывает: формат и длина ролика как у видео (shared/trend-video) — виджет пишет их вместо
 *  выбора, а длина ещё и задаёт цену. Сбой не запоминаем: следующий вход в тренд спросит снова. */
export function cardReferenceMeta(sourceId: string): Promise<CardReferenceMeta | null> {
	const hit = metaCache.get(sourceId)
	if (hit) return hit
	const req = api
		.request<{ ok: boolean; seconds?: number | null; width?: number | null; height?: number | null }>(
			`/api/v1/cards/${encodeURIComponent(sourceId)}/reference/video/meta`,
		)
		.then((r) => {
			const meta = { seconds: positive(r?.seconds), width: positive(r?.width), height: positive(r?.height) }
			// Сервер не смог замерить (ffmpeg не ответил) — ответ без чисел: это сбой, его тоже не запоминаем.
			if (meta.seconds == null && meta.width == null) metaCache.delete(sourceId)
			return meta
		})
		.catch(() => {
			metaCache.delete(sourceId)
			return null
		})
	metaCache.set(sourceId, req)
	return req
}
